import { getApiEndpoints } from '@/services/api/endpoints';
import type { CreateItemResponse } from '@/services/api/types';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type { ISupportMessage } from '@/domain/entities/support';
import type {
  FetchSupportTicketsByPropertyArgs,
  FetchSupportTicketsByStatusArgs,
  ReplyToSupportTicketRequest,
  SupportStatusUpdateRequest,
  SupportTicketListResponse,
  SupportTicketThreadResponse,
  UpdateSupportTicketStatusResponse,
} from './supportTypes';

function hasDataEnvelope<T>(value: T | CreateItemResponse<T>): value is CreateItemResponse<T> {
  return Boolean(value && typeof value === 'object' && 'data' in value);
}

export const fetchSupportTicketsByStatus = createAppAsyncThunk<
  SupportTicketListResponse,
  FetchSupportTicketsByStatusArgs
>('support/fetchByStatus', async ({ status, limit = 25, nextToken }, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  try {
    const params = new URLSearchParams({ status, limit: String(limit) });
    if (nextToken) params.set('nextToken', nextToken);

    const endpoint = `${getApiEndpoints().core.support.getTickets}?${params.toString()}`;
    return await apiService.get<SupportTicketListResponse>(endpoint);
  } catch (err) {
    const error = err as Error & { body?: string };
    return rejectWithValue({ message: error.message || 'Failed to fetch support tickets' });
  }
});

export const fetchSupportTicketsByProperty = createAppAsyncThunk<
  SupportTicketListResponse,
  FetchSupportTicketsByPropertyArgs
>('support/fetchByProperty', async ({ propertyID, limit = 25, nextToken }, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  try {
    const params = new URLSearchParams({ propertyID, limit: String(limit) });
    if (nextToken) params.set('nextToken', nextToken);

    const endpoint = `${getApiEndpoints().core.support.getTickets}?${params.toString()}`;
    return await apiService.get<SupportTicketListResponse>(endpoint);
  } catch (err) {
    const error = err as Error & { body?: string };
    return rejectWithValue({ message: error.message || 'Failed to fetch property support tickets' });
  }
});

export const fetchSupportTicketThread = createAppAsyncThunk<SupportTicketThreadResponse, { ticketID: string }>(
  'support/fetchThread',
  async ({ ticketID }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = `${getApiEndpoints().core.support.getTickets}?id=${encodeURIComponent(ticketID)}`;
      return await apiService.get<SupportTicketThreadResponse>(endpoint);
    } catch (err) {
      const error = err as Error & { body?: string };
      return rejectWithValue({ message: error.message || 'Failed to load support ticket thread' });
    }
  },
);

export const replyToSupportTicket = createAppAsyncThunk<ISupportMessage, ReplyToSupportTicketRequest>(
  'support/reply',
  async (payload, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.support.replyToTicket;
      const response = await apiService.put<
        ISupportMessage | CreateItemResponse<ISupportMessage>,
        ReplyToSupportTicketRequest
      >(endpoint, {
        ...payload,
        photos: payload.photos ?? null,
      });

      return hasDataEnvelope(response) ? response.data : response;
    } catch (err) {
      const error = err as Error & { body?: string };
      return rejectWithValue({ message: error.message || 'Failed to reply to support ticket' });
    }
  },
);

export const updateSupportTicketStatus = createAppAsyncThunk<
  UpdateSupportTicketStatusResponse,
  SupportStatusUpdateRequest
>('support/updateStatus', async (payload, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  try {
    const endpoint = getApiEndpoints().core.support.updateTicketStatus;
    return await apiService.patch<UpdateSupportTicketStatusResponse, SupportStatusUpdateRequest>(endpoint, payload);
  } catch (err) {
    const error = err as Error & { body?: string };
    return rejectWithValue({ message: error.message || 'Failed to update support ticket status' });
  }
});
